import type { ComponentProps } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { mediaSrc } from '../lib/media';
import { ensureAccess } from '../lib/token-store';

/**
 * Session-gated media that survives an expired access token
 * ([ADR 0027](../../../../docs/adr/0027-login-gated-auction-surface.md)).
 *
 * Every vehicle photo now needs a session, and a browser cannot put a header on an `<img src>`,
 * so the access token rides the query string — where it is a **snapshot**, frozen into the URL at
 * render time. The token lives ~15 minutes and is only rotated by `ensureAccess()`, which nothing
 * calls unless a REST request goes out. The auction and vehicle pages don't poll (they reconcile
 * off WebSocket events, and a quiet lot sends none) and a hibernatable socket can stay open for
 * hours, so a page left open outlives its token. Anything that loads *after* that moment — a lazy
 * thumbnail scrolled into view, a gallery photo the visitor clicks — would fetch with the expired
 * token and render as broken, even though the refresh token still represents a valid session.
 *
 * REST doesn't have this problem: `api()` catches the 401, refreshes once and retries. This is the
 * same recovery for media, at the only place that learns of the failure — the element's `error`
 * event. Refresh, rebuild the URL, and the browser reloads it.
 *
 * **One retry per failure, deliberately — not one per path for all time.** `error` also fires for
 * a genuinely missing object (a 404 from a row whose R2 bytes are gone), and an unconditional
 * retry there is an infinite reload loop. So the guard is spent on failure and re-armed by a
 * **successful load**: a 404 errors, retries once, errors again and stops (no success in between),
 * while a path that genuinely recovered is eligible again. Without the re-arm, one path could only
 * ever recover once — a gallery open long enough for the browser's own `max-age=3600` copy to
 * lapse and a second token to expire would leave that photo broken for good. The guard is also
 * keyed to the path, so switching gallery photos never inherits a sibling's spent retry.
 *
 * It is banked **only when a refresh returned a token**, so a transient `/auth/refresh` failure
 * (429/5xx, offline — which keeps the session on purpose) doesn't spend it either. Note what that
 * does and does not buy: nothing here re-requests the image on its own, so the element still
 * recovers on the next load attempt (a remount, or a path change) rather than spontaneously. What
 * it avoids is the guard being *already spent* when that attempt comes.
 *
 * A proactive refresh timer would avoid the momentary blank frame, but it would rotate the
 * refresh token on every open tab whether or not anything needed it — and rotation is one-time-use
 * with a reuse-grace theft response (`token-store.ts`), so more rotations means more chances to
 * trip it. Recovering on demand costs one blank frame in the rare case and no background traffic
 * in the common one. The durable fix is signed media URLs, deferred in ADR 0027's alternatives.
 */
export function useMediaSrc(path: string | null | undefined): {
  src: string | undefined;
  onError: () => void;
  onLoad: () => void;
} {
  const [attempt, setAttempt] = useState(0);
  const retriedFor = useRef<string | null>(null);

  // `attempt` is in the dep list on purpose and unused in the body: bumping it is what re-runs
  // `mediaSrc`, which reads the *current* token out of the store. Without it the memo would keep
  // handing back the URL built from the expired one.
  const src = useMemo(() => mediaSrc(path), [path, attempt]);

  const onError = useCallback(() => {
    if (!path || retriedFor.current === path) return;
    void ensureAccess().then((token) => {
      // The retry is banked only once a refresh actually produced a token. `refreshAccess`
      // resolves `null` for a transient 429/5xx or transport failure and **deliberately keeps the
      // session** (token-store.ts), so spending the guard there would burn this path's one chance
      // on an outage that had nothing to do with it. A 404 still spends it — the token is valid,
      // so `ensureAccess` hands one straight back — which is what keeps a missing object from
      // looping.
      if (!token) return;
      retriedFor.current = path;
      setAttempt((a) => a + 1);
    });
  }, [path]);

  // Re-arm on a successful load. Only a success clears the guard, which is what keeps a 404 from
  // looping — it never gets here.
  const onLoad = useCallback(() => {
    retriedFor.current = null;
  }, []);

  return { src, onError, onLoad };
}

/**
 * An `<img>` for a session-gated media path, wired to {@link useMediaSrc}. Exists so a list of
 * images can each own a retry: the hook cannot be called from inside a `.map()` whose length
 * changes between renders.
 *
 * Renders nothing without a path — callers that want a placeholder (see `VehicleThumb`) decide
 * what that looks like, because "no photo yet" and "photo failed" are different messages.
 */
export function MediaImg({
  path,
  ...rest
}: { path: string | null | undefined } & Omit<ComponentProps<'img'>, 'src' | 'onError'>) {
  const { src, onError, onLoad } = useMediaSrc(path);
  if (!src) return null;
  return <img src={src} onError={onError} onLoad={onLoad} {...rest} />;
}
